import { type FormikProps } from "formik";
import React, { type FC } from "react";
import { connect, type ConnectedProps } from "react-redux";

import { type AssessmentContentView } from "../../types/state";
import { type ContentFormPropsOwn } from "../types";
import { type AppDispatch, type RootState } from "features/Application/globaltypes/redux";
import { ValidatedForm } from "../../../../../components/forms";
import { WithSelfValidation } from "../../../Common/Fields/WithSelfValidation";
import { AssessmentAnswersBuilder, AssessmentQuestionBuilder } from "./AssessmentBuilders";
import { getQuestionDefaultData } from "./AssessmentProviders/defaultQuestionProvider";
import { bindActionCreators } from "redux";

import * as validationActions from "../../state/actions/assessmentContentValidatorActions";
import validationSchemas from "../../../../../utils/validationSchemas";
import QuestionForm from "features/Library/Common/QuestionsForm/QuestionForm";
import QuestionsBuilder from "features/Library/Common/QuestionsForm/Questions/QuestionsBuilder/QuestionsBuilder";
import { assessmentsStateSelector } from "../../state/selectors";
import { useFormikValidation } from "../../../Common/Hooks/useFormikValidationHook";
import { ViewContentLinkButton } from "components";
import { getContentLinkToEupFromPreview } from "utils/eupUtils";
import { AssessmentQuestionHeader } from "./AssessmentQuestionHeader/AssessmentQuestionHeader";

export type ContentFormPropsAll = ContentFormPropsOwn & FormikProps<AssessmentContentView> & PropsFromRedux;

export const AssessmentContentForm: FC<ContentFormPropsAll> = (props: ContentFormPropsAll) => {
  const { isLoading, errors, activeErrors, validateActions, isFormValid, touchedSnapshot, resolved, initialized } =
    props;

  useFormikValidation({ errors, resolved, initialized, activeErrors, isFormValid }, { ...validateActions });

  const renderFields = (): React.ReactNode => {
    const { values, onBlur, save, disabled } = props;

    return (
      <WithSelfValidation
        {...props}
        validationSchema={validationSchemas.assessmentContent}
        render={(validatedFieldProps) => (
          <QuestionForm
            renderQuestionBuilder={(arrayHelpers) => (
              <QuestionsBuilder
                defaultDataProvider={getQuestionDefaultData}
                onBlur={onBlur}
                onChanged={save}
                questions={values.questions}
                arrayHelpers={arrayHelpers}
                validatedFieldProps={{ ...validatedFieldProps, touched: !resolved ? touchedSnapshot : {} }}
                disabled={disabled}
                QuestionHeader={AssessmentQuestionHeader}
                QuestionRenderer={AssessmentQuestionBuilder}
                AnswersBuilder={AssessmentAnswersBuilder}
                propertyNamePrefix={""}
              />
            )}
          />
        )}
      />
    );
  };

  return (
    <>
      {props.hasBeenPublished && (
        <ViewContentLinkButton href={getContentLinkToEupFromPreview["Assessments"](props.id)} fitted />
      )}
      <ValidatedForm
        dirty={props.dirty}
        isLoading={isLoading}
        formWidthComputer={16}
        withoutFormik
        disablePreventTransitionPrompt
      >
        {renderFields}
      </ValidatedForm>
    </>
  );
};

/* istanbul ignore next */
const mapStateToProps = (state: RootState) => {
  const base = assessmentsStateSelector(state).base;

  return {
    ...base.assessmentContentValidatorReducer,
  };
};

/* istanbul ignore next */
const mapDispatchToProps = (dispatch: AppDispatch) => ({
  validateActions: bindActionCreators(validationActions, dispatch),
});

/* istanbul ignore next */
const connector = connect(mapStateToProps, mapDispatchToProps);
type PropsFromRedux = ConnectedProps<typeof connector>;
const ConnectedComponent = connector(AssessmentContentForm);
export default ConnectedComponent;
